// 78 §4 (AUTH-1, AUTH-11) — WHAT EVERY LOGIN SHAPE MUST CARRY.
//
// [RULED] "Three mutually distinguishable login shapes, ONE MODAL, one look and
//          feel. Sequencing across PRs is permitted; three different
//          look-and-feels are not."
//
// This file is what makes that ruling mechanical rather than remembered. The
// modal (`src/components/AuthStepModal.tsx`) renders whatever is in this table
// and knows nothing about TOTP; adding passkeys or passwordless later is an
// ENTRY HERE plus its strings, and the modal is not touched. If a shape were
// added without its four texts, `SHAPE_TEXT_KEYS` would give the modal nothing
// to render — which is why the keys are listed rather than derived from the id.
//
// [RULED — AUTH-11] every shape carries FOUR texts, in en/fr/nl:
//   1. how it works
//   2. its risks, INCLUDING WHERE IT DOES NOT PROTECT
//   3. the firm-admin fallback
//   4. the seller fallback — which has two forms, cloud and local installation
//
// [RULED — AUTH-10] the local-installation variant MUST NOT BE SOFTENED. It is
// the one that tells a person nobody is coming.

export type AuthShapeId = "totp" | "webauthn" | "passwordless";

export interface AuthShape {
  id: AuthShapeId;
  /** The shape's name, as a person reads it. */
  nameKey: string;
  /** AUTH-11's four texts. */
  howKey: string;
  risksKey: string;
  fallbackFirmKey: string;
  fallbackSellerCloudKey: string;
  fallbackSellerLocalKey: string;
  /**
   * 83 §2 — the permissive-mode variants, on the one shape that has them.
   * Optional because two of the three shapes do not: a tenant setting changes
   * what shape (c) is HONESTLY CALLED, and nothing about (a) or (b).
   */
  permissiveNameKey?: string;
  permissiveHowKey?: string;
  permissiveRisksKey?: string;
}

/**
 * 83 §2 (AUTH-6) — SHAPE (c) READS DIFFERENTLY IN THE TWO TENANT MODES, AND
 * THAT IS A RULING RATHER THAN A PREFERENCE.
 *
 * "In PERMISSIVE mode the shape MUST NOT be labelled 'passwordless' anywhere in
 * the UI — it is *passkey sign-in (password still active)* — and its risk text
 * must state plainly that the account's strength is that of shape (a). The name
 * carries the truth so the setting cannot quietly lie."
 *
 * THREE of the six texts change, not one. The name is the ruling's own example;
 * the risk text is named in the ruling too; and `how` goes with them because
 * under STRICT it has to say the password is GONE and under PERMISSIVE that
 * would be false. A shape whose "how it works" lies is not saved by an honest
 * label above it.
 *
 * The other three — both fallbacks and the firm-admin one — do not change,
 * because what an administrator or the publisher can do is the same either way.
 */
export interface PasswordlessModeTexts {
  nameKey: string;
  howKey: string;
  risksKey: string;
}

export type PasswordlessMode = "strict" | "permissive";

// The order here is the order a person is offered them when a tenant permits
// more than one, and until 82 the question could not arise because there was
// one. TOTP STAYS FIRST, which is a decision and not an accident: a passkey is
// the stronger shape — bound to the domain, so it cannot be handed to a
// phishing site the way six typed digits can — and leading with it would be
// defensible. It is not taken here because this slice adds a shape and does not
// re-rank the existing one; a tenant that wants the passkey offered first is a
// setting, not a constant, and nobody has asked for it yet.
export const AUTH_SHAPES: AuthShape[] = [
  {
    id: "totp",
    nameKey: "auth.shape.totp.name",
    howKey: "auth.shape.totp.how",
    risksKey: "auth.shape.totp.risks",
    fallbackFirmKey: "auth.shape.totp.fallback_firm",
    fallbackSellerCloudKey: "auth.shape.totp.fallback_seller_cloud",
    fallbackSellerLocalKey: "auth.shape.totp.fallback_seller_local",
  },
  {
    // 82 §4 — shape (b), password then passkey. The ENTRY the file's own header
    // promised: "adding passkeys or passwordless later is an ENTRY HERE plus
    // its strings, and the modal is not touched." It was, once, and only to add
    // the branch that calls the authenticator — there is no second look and
    // feel, which is what AUTH-1 forbids.
    id: "webauthn",
    nameKey: "auth.shape.webauthn.name",
    howKey: "auth.shape.webauthn.how",
    risksKey: "auth.shape.webauthn.risks",
    fallbackFirmKey: "auth.shape.webauthn.fallback_firm",
    fallbackSellerCloudKey: "auth.shape.webauthn.fallback_seller_cloud",
    fallbackSellerLocalKey: "auth.shape.webauthn.fallback_seller_local",
  },
  {
    // 83 §2 — shape (c), the passkey alone. The third and last of AUTH-1's
    // three, and again an ENTRY HERE rather than a second look and feel.
    //
    // ITS DEFAULT KEYS ARE THE STRICT ONES. That is deliberate: anything that
    // reads this table without knowing about the mode — a future screen, a
    // careless call — gets the honest "passwordless" name, which is only ever
    // wrong in the direction of a tenant that has MORE protection than the label
    // claims. The permissive keys are reachable only through
    // `passwordlessTextKeys()`, which has to be given the mode to answer.
    id: "passwordless",
    nameKey: "auth.shape.passwordless.name",
    howKey: "auth.shape.passwordless.how",
    risksKey: "auth.shape.passwordless.risks",
    fallbackFirmKey: "auth.shape.passwordless.fallback_firm",
    fallbackSellerCloudKey: "auth.shape.passwordless.fallback_seller_cloud",
    fallbackSellerLocalKey: "auth.shape.passwordless.fallback_seller_local",
    // The permissive variants live HERE, in the table, and not inside the
    // function that chooses between them. That is not tidiness — it is what
    // puts them inside `scripts/lint/auth-shape-texts.js`'s reach. Declared in
    // the function, they were unguarded: [MEASURED 2026-08-15] deleting
    // `risks_permissive` from the seed left that lint GREEN, which is the exact
    // blind spot the lint's own header was written about ("both i18n rules walk
    // LITERAL first arguments … these keys are never literals at a call site").
    // A ruling carried by an unguarded key is a ruling one bad merge from gone.
    permissiveNameKey: "auth.shape.passwordless.name_permissive",
    permissiveHowKey: "auth.shape.passwordless.how_permissive",
    permissiveRisksKey: "auth.shape.passwordless.risks_permissive",
  },
];

/**
 * The three mode-dependent keys for shape (c). The MIRROR of
 * `server/src/lib/authPolicy.js passwordlessTextKeys` — same rule, both sides.
 *
 * A caller asks for "the texts for this tenant" and cannot get at the strict
 * name in a permissive tenant, because there is no argument that returns it.
 * That is what makes AUTH-6 mechanical instead of remembered: the rule is not
 * "do not write the word", it is "the word is not reachable from here".
 *
 * Unknown or missing modes fall to STRICT, which is the safe direction — see
 * the table entry above.
 */
export function passwordlessTextKeys(
  mode: string | null | undefined,
  shape: AuthShape | null = shapeById("passwordless"),
): PasswordlessModeTexts | null {
  if (!shape) return null;
  const strict = { nameKey: shape.nameKey, howKey: shape.howKey, risksKey: shape.risksKey };
  if (mode !== "permissive") return strict;
  // A variant that is somehow absent falls back to the STRICT key rather than
  // to nothing: an honest name on a tenant that deserves a gentler one is a
  // cosmetic fault, whereas an empty label is a screen with a hole in it. The
  // lint is what stops this arm ever being reached.
  return {
    nameKey: shape.permissiveNameKey || strict.nameKey,
    howKey: shape.permissiveHowKey || strict.howKey,
    risksKey: shape.permissiveRisksKey || strict.risksKey,
  };
}

/**
 * The whole shape as this tenant must present it — the table entry with its
 * three mode-dependent keys replaced. Screens render THIS and never the raw
 * table entry, so a permissive tenant cannot show a strict text by omission.
 */
export function shapeForMode(shape: AuthShape, mode: string | null | undefined): AuthShape {
  if (shape.id !== "passwordless") return shape;
  return { ...shape, ...(passwordlessTextKeys(mode, shape) || {}) };
}

export const shapeById = (id: string): AuthShape | null =>
  AUTH_SHAPES.find((s) => s.id === id) || null;

/**
 * WHICH SELLER FALLBACK IS TRUE HERE — and this is a SEAM, not a feature.
 *
 * The two texts say opposite things and only one of them may be shown: on a
 * cloud tenant the publisher can clear a factor on the server; on a local
 * installation nobody can, because the copy runs on the firm's own equipment
 * and the publisher cannot reach it.
 *
 * [MEASURED 2026-08-13] NOTHING IN THIS TREE KNOWS WHICH IT IS. There is no
 * edition flag, no `installation` setting, and the pack format
 * (server/src/lib/packFormat.js) does not carry one — the local tier is ruled
 * (RULINGS.md, brief 04 §1) and registered, but it has no runtime signal yet.
 *
 * So this reads a field the config route does not yet answer and defaults to
 * 'cloud', which is what staging and prod are. BOTH texts ship, in all three
 * languages, unsoftened; the day `/api/auth/config` answers
 * `installation: 'local'` the right one appears with no change here and none in
 * the modal. Inventing that flag inside this brief would be scope it does not
 * ask for, so it is named as owed instead of guessed at.
 */
export type InstallationTier = "cloud" | "local";

/** The shape of `/api/auth/config` this file reads — one field, and it is optional. */
export interface AuthConfigLike {
  installation?: string;
  /** 83 §2 (AUTH-6) — 'strict' | 'permissive'. Decides what shape (c) is CALLED. */
  passwordlessMode?: string;
  /** Whether this tenant offers shape (c) at all. */
  passwordless?: boolean;
  /** 83 §3 — which shapes this firm permits, for the invitation screen. */
  authMethodsEnabled?: string[];
}

export function installationTier(cfg: AuthConfigLike | null | undefined): InstallationTier {
  return cfg && cfg.installation === "local" ? "local" : "cloud";
}

/** The seller-fallback key that is TRUE for this installation. */
export const sellerFallbackKey = (shape: AuthShape, tier: InstallationTier): string =>
  tier === "local" ? shape.fallbackSellerLocalKey : shape.fallbackSellerCloudKey;

/* BRIEF 96a §3a — `CollaboratorLike`, REHOMED.
 *
 * It lived in `src/components/AuthFactorsAdminPanel.tsx`, a component brief 81
 * §8 superseded and unmounted. Two files imported the interface and nothing
 * else imported anything, so a retired screen stayed in the tree to host a
 * type — and that is the whole reason brief 96 read the firm-admin fallback as
 * missing a door it has had since 81. The type outlived its component; it
 * belongs where the auth types already live.
 *
 * Deliberately narrow, and the original's reason is kept verbatim because it is
 * still the reason: a screen that lists people and asks the server about one of
 * them has no business knowing the rest of the row.
 */
export interface CollaboratorLike {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  coType?: string | null;
}
